import type { Pagina } from '@/modulos/operacao/comum/contrato';
import { ErroApi } from '@/nucleo/auth/cliente-api';

/**
 * O que as telas de titulares, veículos, condutores e monitores recebem da API
 * além da linha (`apps/api/src/modulos/consultas/leitura-de-recursos.ts`).
 *
 * O RECORTE VEM DO BANCO. A API roda as consultas sob as políticas da 0012: o
 * papel global vê tudo, o de diretoria vê o que é de credenciado da própria
 * DRE. A tela não sabe nem decide a DRE de ninguém.
 *
 * O QUE O PERFIL LÊ ALÉM DO CADASTRO TAMBÉM VEM DO BANCO, em `permissoes`, e a
 * tela só mostra o que o perfil lê (regra 7 de interface):
 *   - `documentacao`: a documentação da J-05 (validade aprovada de cada
 *     documento) só abre a papel global (0063, 0066);
 *   - `ordensDeServico`: a OS da jornada só abre a quem `os_pode_ler()` (0254).
 * Sem a permissão, a coluna, o filtro ou a aba nem aparecem — mostrar vazio
 * diria "nenhum documento vencido" a quem simplesmente não pode ver.
 */
export interface PermissoesDeLeitura {
  documentacao: boolean;
  ordensDeServico: boolean;
}

export interface PaginaComPermissoes<T> extends Pagina<T> {
  permissoes: PermissoesDeLeitura;
}

export const SEM_PERMISSOES: PermissoesDeLeitura = { documentacao: false, ordensDeServico: false };

/** Um documento da J-05 do recurso: a última versão de cada tipo. */
export interface DocumentoDoRecurso {
  id: string;
  tipo_documento: string;
  versao: number;
  /** `EM_ANALISE`, `VALIDO`, `RECUSADO` ou `SUBSTITUIDO` (0063). */
  status: string;
  /** Título no catálogo do edital; nulo quando o tipo não está lá. */
  titulo: string | null;
  /** Validade aprovada pelo DTP (`recurso_documento_decisoes`), ISO `YYYY-MM-DD`. */
  validade: string | null;
  enviado_em: string;
}

/** Uma OS da jornada em que o recurso entrou (0254). */
export interface OsDoRecurso {
  id: string;
  numero_completo: string | null;
  status: string;
  inicio: string;
  fim: string;
  situacao_vigencia: 'A_INICIAR' | 'VIGENTE' | 'ENCERRADA' | null;
}

/**
 * Campos que o detalhe de veículo, condutor e monitor traz além do cadastro.
 * `documentos` e `ordens` são nulos quando o perfil não os lê.
 */
export interface ExtrasDoDetalhe {
  documentos: DocumentoDoRecurso[] | null;
  ordens: OsDoRecurso[] | null;
  permissoes: PermissoesDeLeitura;
}

/** Estado do cadastro na J-05 (0063). `LEGADO` é o que veio antes da jornada. */
export const ROTULO_DO_ESTADO_DO_CADASTRO: Record<string, string> = {
  LEGADO: 'Legado',
  EM_ANALISE: 'Em análise',
  APROVADO: 'Aprovado',
  REPROVADO: 'Reprovado',
};

/**
 * O detalhe que não existe (ou que o recorte não mostra) responde 404: a tela
 * trata como "não encontrado", não como falha de sistema.
 */
export function nuloSeNaoEncontrado(erro: unknown): null {
  if (erro instanceof ErroApi && erro.situacao === 404) {
    return null;
  }
  throw erro;
}
